import React, { useEffect, useRef, useState } from "react";
import { Tour } from "../../../components/helpers/types/modal_habitacion";

import { formatearNumero } from "../../../components/helpers/formatearNumero";
import { usePriceToSelectedCurrency } from "../../../hooks/usePriceToSelectedCurrency";
import useCurrencyPrices from "../../../hooks/useCurrencyPrices";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";
import Swal from "sweetalert2";

interface Servicio {
    id: number;
    nombre: string;
    precio: number;
}

interface Props {
    modalVisible: boolean;
    setModalVisible: (b: boolean) => void;
    tipo_moneda: string;
    id_habitacion: number;
    id_pedido: number;
    getOnePedido: () => Promise<void>;
}

const ModalAgregarServicio = ({
    modalVisible,
    setModalVisible,
    tipo_moneda,
    id_habitacion,
    id_pedido,
    getOnePedido,
}: Props) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { price_to_currency } = usePriceToSelectedCurrency();
    const [servicios, setServicios] = useState<Servicio[]>([]);
    const [selectedServicios, setSelectedServicios] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    const getServicios = async () => {
        setLoading(true);

        try {
            const response = await fetchAxiosToken({
                url: `/servicios_adicionales_habitaciones/getAll/${id_habitacion}`,
                method: "get",
            });

            setServicios(response);
        } catch (error) {
            console.error("Error al obtener habitaciones:", error);
        } finally {
            setLoading(false);
        }
    };

    // Maneja los checkbox
    const handleSelectServicio = (id: number) => {
        setSelectedServicios((prev) =>
            prev.includes(id) ? prev.filter((sId) => sId !== id) : [...prev, id]
        );
    };

    // Registrar los servicios seleccionados
    const handleRegistrarServicios = async () => {
        if (selectedServicios.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "Selecciona al menos un servicio adicional",
                confirmButtonText: "Aceptar",
            });
            return;
        }

        setLoading(true);
        try {
            // Obtener los servicios seleccionados
            const serviciosARegistrar = servicios
                .filter((s) => selectedServicios.includes(s.id))
                .map((s) => ({
                    servicio: s.nombre,
                    costo: price_to_currency(Number(s.precio), tipo_moneda),
                }));

            await fetchAxiosToken({
                url: `/pedidos_habitaciones/putPedidoAddAditionalService/${id_pedido}`,
                method: "put",
                body: { servicio: serviciosARegistrar },
            });

            await getOnePedido();
            setModalVisible(false);
            setSelectedServicios([]);
        } catch (error) {
            console.error("Error al registrar servicios adicionales:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (modalVisible) {
            getServicios();
        }
    }, [modalVisible]);

    return (
        <ResponsiveModalAbsolute
            scrollRef={scrollRef}
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            max_width={90}
        >
            <div className="flex flex-col w-full h-full gap-6 p-4 lg:py-5 lg:px-16">
                <h2 className="text-lg font-bold text-[#1C4CBB] mb-2">
                    Selecciona los servicios adicionales
                </h2>

                <div className="grid 2xl:grid-cols-4 xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
                    {loading ? (
                        <p className="text-center col-span-full">Cargando...</p>
                    ) : servicios.length === 0 ? (
                        <p className="text-center col-span-full">
                            No hay servicios disponibles.
                        </p>
                    ) : (
                        servicios.map((servicio) => (
                            <label
                                key={servicio.id}
                                className={`border rounded-lg p-4 shadow-md flex flex-col gap-3 cursor-pointer transition-all hover:scale-105 ${
                                    selectedServicios.includes(servicio.id)
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200"
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedServicios.includes(
                                            servicio.id
                                        )}
                                        onChange={() =>
                                            handleSelectServicio(servicio.id)
                                        }
                                        className="w-5 h-5 accent-blue-600 cursor-pointer"
                                    />
                                    <span className="font-semibold text-[#1C4CBB]">
                                        {servicio.nombre}
                                    </span>
                                </div>

                                <div className="text-right font-semibold text-[#1C4CBB]">
                                    {tipo_moneda}{" "}
                                    {formatearNumero(
                                        price_to_currency(
                                            Number(servicio.precio),
                                            tipo_moneda
                                        )
                                    )}
                                </div>
                            </label>
                        ))
                    )}
                </div>

                <div className="flex justify-end mt-6">
                    <button
                        onClick={handleRegistrarServicios}
                        disabled={loading || selectedServicios.length === 0}
                        className={`px-6 py-2 rounded-lg font-semibold text-white transition-all duration-200 ${
                            selectedServicios.length === 0
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {loading
                            ? "Registrando..."
                            : "Registrar servicios seleccionados"}
                    </button>
                </div>
            </div>
        </ResponsiveModalAbsolute>
    );
};

export default ModalAgregarServicio;
